import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Truck, Plus, Trash2, Upload, FileText, AlertCircle, CheckCircle,
  Calendar, Shield, ChevronDown, ChevronUp, Clock, XCircle,
  Search, RefreshCw, BadgeCheck, Gauge, History,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, vehiclesService, documentsService } from '../services/api';
import { VehicleType, DocumentType, MotStatus } from '../types';
import type { UserVehicle, DocumentUpload, VehicleLookupResult, MotTest } from '../types';
import { normaliseRegistration, validateRegistration, MAX_REGISTRATION_LENGTH } from '../lib/registrationPlate';

// ─── Business Insurance ───────────────────────────────────────────────────────

interface InsuranceMeta {
  type: DocumentType;
  label: string;
  hint: string;
}

const BUSINESS_INSURANCE_DOCS: InsuranceMeta[] = [
  {
    type: DocumentType.GoodsInTransitInsurance,
    label: 'Goods in Transit (GIT) Insurance',
    hint: 'Upload your GIT insurance certificate showing coverage for goods you transport.',
  },
  {
    type: DocumentType.PublicLiabilityInsurance,
    label: 'Public Liability Insurance',
    hint: 'Upload your public liability insurance certificate (minimum £1M coverage recommended).',
  },
];

function InsuranceStatusBadge({ status }: { status: string }) {
  if (status === 'Approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 shrink-0">
      <CheckCircle className="w-3 h-3" />Approved
    </span>
  );
  if (status === 'Rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 shrink-0">
      <XCircle className="w-3 h-3" />Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 shrink-0">
      <Clock className="w-3 h-3" />Pending Review
    </span>
  );
}

function BusinessInsuranceCard({
  meta,
  uploads,
  getToken,
  onUploadDone,
}: {
  meta: InsuranceMeta;
  uploads: DocumentUpload[];
  getToken: () => Promise<string>;
  onUploadDone: () => void;
}) {
  const [file, setFile]           = useState<File | null>(null);
  const [expiryDate, setExpiry]   = useState(defaultExpiryDate());
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [success, setSuccess]     = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const today = new Date().toISOString().split('T')[0];

  const sorted  = [...uploads].sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  const latest  = sorted[0];
  const history = sorted.slice(1);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!expiryDate) {
      setError('Expiry date is required before uploading.');
      return;
    }
    setFile(f);
    setUploading(true); setError(null); setSuccess(false);
    try {
      await documentsService(createApiClient(getToken)).upload(f, meta.type, 'Carrier', expiryDate);
      setSuccess(true); setFile(null); setExpiry(defaultExpiryDate());
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploadDone();
    } catch (err: any) {
      setError(err?.response?.data?.title ?? err?.message ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm">{meta.label}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{meta.hint}</p>
        </div>
        {latest
          ? <InsuranceStatusBadge status={latest.reviewStatus} />
          : <span className="text-xs text-gray-400 shrink-0">Not submitted</span>
        }
      </div>

      {latest && (
        <div className={`px-5 py-2.5 border-t border-b text-xs flex flex-col gap-1 ${latest.reviewStatus === 'Rejected' ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
          <div className="flex items-center gap-2 text-gray-600">
            <FileText className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span className="truncate font-medium">{latest.originalFileName}</span>
            <span className="text-gray-400 shrink-0">· {new Date(latest.uploadedAt).toLocaleDateString('en-GB')}</span>
            {latest.expiryDate && (
              <span className="text-gray-400 shrink-0">· Expires {new Date(latest.expiryDate).toLocaleDateString('en-GB')}</span>
            )}
          </div>
          {latest.reviewStatus === 'Rejected' && latest.reviewNotes && (
            <p className="text-red-600 pl-5"><span className="font-medium">Rejected: </span>{latest.reviewNotes}</p>
          )}
          {history.length > 0 && (
            <button type="button" onClick={() => setShowHistory(h => !h)} className="text-blue-500 hover:text-blue-700 pl-5 text-left">
              {showHistory ? 'Hide' : 'Show'} {history.length} previous version{history.length > 1 ? 's' : ''}
            </button>
          )}
          {showHistory && history.map(u => (
            <div key={u.documentId} className="pl-5 flex items-center gap-2 text-gray-400">
              <FileText className="w-3 h-3 shrink-0" />
              <span className="truncate">{u.originalFileName}</span>
              <span className="shrink-0">· {new Date(u.uploadedAt).toLocaleDateString('en-GB')}</span>
              <InsuranceStatusBadge status={u.reviewStatus} />
            </div>
          ))}
        </div>
      )}

      <div className="px-5 py-4 space-y-3">
        {latest && !success && (
          <p className="text-xs text-gray-500 font-medium">
            {latest.reviewStatus === 'Rejected' ? 'Upload a corrected document:' : 'Upload a replacement:'}
          </p>
        )}
        {/* Expiry date — required, shown before file upload */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Expiry date <span className="text-red-500">*</span>
          </label>
          <div className="relative w-44">
            <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            <input type="date" value={expiryDate} min={today} max={defaultExpiryDate()}
              onChange={e => { setExpiry(e.target.value); setError(null); }}
              className="w-full border border-gray-300 rounded-lg pl-6 pr-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap sm:flex-nowrap">
          <div
            className={`flex-1 min-w-0 border border-dashed rounded-lg px-3 py-2 text-xs flex items-center gap-2 ${
              expiryDate && !uploading
                ? 'border-gray-300 cursor-pointer hover:border-indigo-400 transition-colors'
                : 'border-gray-200 opacity-50 cursor-not-allowed'
            }`}
            onClick={() => expiryDate && !uploading && fileInputRef.current?.click()}
          >
            {uploading
              ? <span className="w-3.5 h-3.5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin shrink-0" />
              : <Upload className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            }
            {uploading
              ? <span className="text-indigo-600 font-medium">Uploading…</span>
              : file
                ? <span className="text-gray-700 font-medium truncate">{file.name}</span>
                : <span className="text-gray-400">{latest ? 'Choose replacement file' : 'Choose file to upload'}</span>
            }
          </div>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden"
            onChange={handleFile} />
        </div>
        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{error}
          </div>
        )}
        {success && (
          <div className="flex items-start gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />Submitted for verification.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  [VehicleType.Motorcycle]: 'Motorcycle',
  [VehicleType.Car]:        'Car',
  [VehicleType.SmallVan]:   'Small Van',
  [VehicleType.MidiVan]:    'Midi Van',
  [VehicleType.LWBVan]:     'LWB Van',
  [VehicleType.LutonVan]:   'Luton Van',
  [VehicleType.Tonne7_5]:   '7.5T',
  [VehicleType.Tonne12]:    '12T',
  [VehicleType.Tonne18]:    '18T',
  [VehicleType.Tonne26]:    '26T',
  [VehicleType.Artic]:      'Artic',
};

const VEHICLE_TYPES = [
  VehicleType.Motorcycle, VehicleType.Car,
  VehicleType.SmallVan,   VehicleType.MidiVan, VehicleType.LWBVan, VehicleType.LutonVan,
  VehicleType.Tonne7_5,   VehicleType.Tonne12, VehicleType.Tonne18, VehicleType.Tonne26,
  VehicleType.Artic,
];

function expiryClass(dateStr?: string): string {
  if (!dateStr) return 'text-gray-400';
  const d = new Date(dateStr);
  const now = new Date();
  const days = Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
  if (days < 0) return 'text-red-600 font-semibold';
  if (days <= 30) return 'text-amber-600 font-semibold';
  return 'text-green-700';
}

function defaultExpiryDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 13);
  return d.toISOString().split('T')[0];
}

/**
 * NotYetDue is deliberately green, not amber: a vehicle too new to have been tested is
 * fully road-legal, and flagging it as a problem would be wrong.
 */
function MotStatusBadge({ vehicle }: { vehicle: UserVehicle }) {
  const style: Record<MotStatus, string> = {
    [MotStatus.Valid]:        'bg-green-100 text-green-700',
    [MotStatus.NotYetDue]:    'bg-green-100 text-green-700',
    [MotStatus.Expired]:      'bg-red-100 text-red-700',
    [MotStatus.NoDvsaRecord]: 'bg-amber-100 text-amber-800',
    [MotStatus.Unknown]:      'bg-gray-100 text-gray-600',
  };
  const Icon = vehicle.motCompliant ? BadgeCheck : AlertCircle;

  return (
    <span
      data-testid={`vehicle-mot-status-${vehicle.id}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${style[vehicle.motStatus] ?? style[MotStatus.Unknown]}`}
    >
      <Icon className="w-3 h-3" />{vehicle.motSummary}
    </span>
  );
}

const DEFECT_STYLE: Record<string, string> = {
  DANGEROUS: 'bg-red-100 text-red-800 border-red-200',
  MAJOR:     'bg-orange-100 text-orange-800 border-orange-200',
  FAIL:      'bg-orange-100 text-orange-800 border-orange-200',
  MINOR:     'bg-yellow-50 text-yellow-800 border-yellow-200',
  PRS:       'bg-blue-50 text-blue-800 border-blue-200',
  ADVISORY:  'bg-gray-50 text-gray-600 border-gray-200',
};

function MotHistoryPanel({ vehicleId }: { vehicleId: string }) {
  const { getToken } = useAppAuth();
  const { data: tests = [], isLoading } = useQuery<MotTest[]>({
    queryKey: ['mot-history', vehicleId],
    queryFn: () => vehiclesService(createApiClient(getToken)).getMotHistory(vehicleId),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <div className="animate-spin w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <p className="text-xs text-gray-500 py-2">
        No MOT tests recorded. This is normal for a vehicle under three years old.
      </p>
    );
  }

  return (
    <div data-testid={`vehicle-mot-history-${vehicleId}`} className="space-y-2">
      {tests.map(test => (
        <div key={test.id} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 flex items-center gap-2 flex-wrap border-b border-gray-100">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
              test.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {test.passed ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {test.testResult ?? 'UNKNOWN'}
            </span>
            {test.completedDate && (
              <span className="text-xs text-gray-600">
                {new Date(test.completedDate).toLocaleDateString('en-GB')}
              </span>
            )}
            {/* A failed test has no expiry — saying "expires —" would imply data is missing. */}
            {test.expiryDate && (
              <span className="text-xs text-gray-500">
                · valid to {new Date(test.expiryDate).toLocaleDateString('en-GB')}
              </span>
            )}
            {test.odometerValue && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Gauge className="w-3 h-3" />
                {Number(test.odometerValue).toLocaleString('en-GB')} {test.odometerUnit?.toLowerCase() ?? ''}
              </span>
            )}
            {test.motTestNumber && (
              <span className="text-[10px] text-gray-400 ml-auto">#{test.motTestNumber}</span>
            )}
          </div>

          {test.defects.length > 0 && (
            <ul className="px-3 py-2 space-y-1">
              {test.defects.map((d, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold shrink-0 ${
                    DEFECT_STYLE[d.type?.toUpperCase() ?? ''] ?? DEFECT_STYLE.ADVISORY
                  }`}>
                    {d.dangerous ? 'DANGEROUS' : (d.type ?? 'ADVISORY')}
                  </span>
                  <span className="text-xs text-gray-600">{d.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/** Manual MOT upload — shown only when DVSA holds no record for the vehicle. */
function ManualMotUpload({
  vehicleId,
  currentExpiry,
  onUploaded,
}: {
  vehicleId: string;
  currentExpiry?: string;
  onUploaded: () => void;
}) {
  const { getToken } = useAppAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [expiry, setExpiry]       = useState(currentExpiry?.split('T')[0] ?? defaultExpiryDate());
  const fileRef = useRef<HTMLInputElement>(null);
  const today = new Date().toISOString().split('T')[0];

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!expiry) { setError('Expiry date is required before uploading.'); return; }
    setUploading(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      await documentsService(api).upload(file, DocumentType.MotCertificate, 'Carrier', expiry, vehicleId);
      onUploaded();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message ?? 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div data-testid={`vehicle-manual-mot-${vehicleId}`} className="space-y-3 border border-amber-200 bg-amber-50 rounded-lg p-3">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          DVSA holds no MOT record for this vehicle, so we cannot verify it automatically.
          Upload the certificate and our team will review it.
        </p>
      </div>

      <div>
        <label className="block text-xs text-gray-600 mb-1">
          MOT expiry date <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="date"
            value={expiry}
            min={today}
            onChange={e => { setExpiry(e.target.value); setError(null); }}
            className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
        </div>
      </div>

      <button
        onClick={() => { if (!expiry) { setError('Set an expiry date before uploading.'); return; } fileRef.current?.click(); }}
        disabled={uploading || !expiry}
        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 transition-colors disabled:opacity-50"
      >
        {uploading
          ? <span className="animate-spin w-3 h-3 border-2 border-amber-600 border-t-transparent rounded-full" />
          : <Upload className="w-3.5 h-3.5" />
        }
        {uploading ? 'Uploading…' : 'Upload MOT certificate'}
      </button>
      <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleFile} />

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />{error}
        </p>
      )}
    </div>
  );
}

function InsuranceUpload({
  vehicleId,
  currentExpiry,
  onUploaded,
}: {
  vehicleId: string;
  currentExpiry?: string;
  onUploaded: () => void;
}) {
  const { getToken } = useAppAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [expiry, setExpiry]       = useState(currentExpiry?.split('T')[0] ?? defaultExpiryDate());
  const fileRef = useRef<HTMLInputElement>(null);
  const today = new Date().toISOString().split('T')[0];

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!expiry) { setError('Expiry date is required before uploading.'); return; }
    setUploading(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      await documentsService(api).upload(file, DocumentType.HireRewardInsurance, 'Carrier', expiry, vehicleId);
      await vehiclesService(api).updateVehicle(vehicleId, { hireRewardExpiry: expiry });
      onUploaded();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err.message ?? 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Hire &amp; Reward Insurance</span>
        {currentExpiry && (
          <span className={`text-xs ${expiryClass(currentExpiry)}`}>
            Expires {new Date(currentExpiry).toLocaleDateString('en-GB')}
          </span>
        )}
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">
          Expiry date <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <input
            type="date"
            value={expiry}
            min={today}
            onChange={e => { setExpiry(e.target.value); setError(null); }}
            className="text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => { if (!expiry) { setError('Set an expiry date before uploading.'); return; } fileRef.current?.click(); }}
          disabled={uploading || !expiry}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-50"
        >
          {uploading
            ? <span className="animate-spin w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full" />
            : <Upload className="w-3.5 h-3.5" />
          }
          {uploading ? 'Uploading…' : 'Upload document'}
        </button>
        <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleFile} />
      </div>

      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />{error}
        </p>
      )}
    </div>
  );
}

function VehicleCard({
  vehicle,
  onDelete,
  deleting,
  onRefresh,
}: {
  vehicle: UserVehicle;
  onDelete: () => void;
  deleting: boolean;
  onRefresh: () => void;
}) {
  const { getToken } = useAppAuth();
  const [expanded, setExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const qc = useQueryClient();

  const refreshMut = useMutation({
    mutationFn: () => vehiclesService(createApiClient(getToken)).refreshMot(vehicle.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.invalidateQueries({ queryKey: ['mot-history', vehicle.id] });
    },
  });

  const insOk = vehicle.hireRewardExpiry && new Date(vehicle.hireRewardExpiry) > new Date();

  return (
    <div className="card overflow-hidden" data-testid={`vehicle-row-${vehicle.id}`}>
      <div
        className="flex items-center justify-between gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(v => !v)}
        data-testid={`vehicle-expand-${vehicle.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
            <Truck className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 uppercase tracking-wide">{vehicle.registration}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {VEHICLE_TYPE_LABELS[vehicle.vehicleType] ?? 'Vehicle'}
              </span>
              {vehicle.make && (
                <span className="text-sm text-gray-500">{vehicle.make} {vehicle.model}</span>
              )}
              {vehicle.year && <span className="text-xs text-gray-400">{vehicle.year}</span>}
              {vehicle.nickname && <span className="text-xs text-gray-400 italic">“{vehicle.nickname}”</span>}
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <MotStatusBadge vehicle={vehicle} />
              {vehicle.hireRewardExpiry
                ? <span className={`text-xs flex items-center gap-1 ${expiryClass(vehicle.hireRewardExpiry)}`}>
                    {insOk ? <Shield className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    Ins {new Date(vehicle.hireRewardExpiry).toLocaleDateString('en-GB')}
                  </span>
                : <span className="text-xs text-gray-400 flex items-center gap-1"><Shield className="w-3 h-3" />No Insurance</span>
              }
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            disabled={deleting}
            title="Remove vehicle"
            data-testid={`vehicle-delete-${vehicle.id}`}
            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-5 bg-gray-50/50">
          {/* DVSA-sourced details, read-only */}
          {vehicle.make && (
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              {[
                ['Make',   vehicle.make],
                ['Model',  vehicle.model],
                ['Colour', vehicle.colour],
                ['Fuel',   vehicle.fuelType],
                ['Engine', vehicle.engineSizeCc ? `${vehicle.engineSizeCc} cc` : undefined],
                ['First used', vehicle.firstUsedDate ? new Date(vehicle.firstUsedDate).toLocaleDateString('en-GB') : undefined],
                ['Recall',  vehicle.hasOutstandingRecall],
              ].filter(([, v]) => !!v).map(([label, value]) => (
                <div key={label as string}>
                  <dt className="text-gray-400">{label}</dt>
                  <dd className="text-gray-800 font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => refreshMut.mutate()}
              disabled={refreshMut.isPending}
              data-testid={`vehicle-refresh-mot-${vehicle.id}`}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshMut.isPending ? 'animate-spin' : ''}`} />
              {refreshMut.isPending ? 'Checking DVSA…' : 'Re-check MOT'}
            </button>
            <button
              onClick={() => setShowHistory(h => !h)}
              data-testid={`vehicle-history-toggle-${vehicle.id}`}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 transition-colors"
            >
              <History className="w-3.5 h-3.5" />
              {showHistory ? 'Hide MOT history' : 'MOT history'}
            </button>
            {vehicle.motLastCheckedAt && (
              <span className="text-[11px] text-gray-400">
                Last checked {new Date(vehicle.motLastCheckedAt).toLocaleDateString('en-GB')}
              </span>
            )}
          </div>

          {showHistory && <MotHistoryPanel vehicleId={vehicle.id} />}

          {/* Manual upload only where DVSA cannot help — otherwise MOT needs no user action. */}
          {vehicle.requiresManualMot && (
            <ManualMotUpload
              vehicleId={vehicle.id}
              currentExpiry={vehicle.motExpiry}
              onUploaded={onRefresh}
            />
          )}

          <div className="border-t border-gray-100" />
          <InsuranceUpload
            vehicleId={vehicle.id}
            currentExpiry={vehicle.hireRewardExpiry}
            onUploaded={onRefresh}
          />
        </div>
      )}
    </div>
  );
}

/** Registration-first add flow: look the plate up at DVSA, then confirm. */
function AddVehicleForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { getToken } = useAppAuth();
  const [reg, setReg]           = useState('');
  const [vehicleType, setVt]    = useState<VehicleType>(VehicleType.SmallVan);
  // Whether the current selection came from the DVSA lookup rather than the user. Only drives
  // the hint under the picker - the moment the user taps a chip it is theirs, not DVSA's.
  const [typeFromDvsa, setTypeFromDvsa] = useState(false);
  const [nickname, setNickname] = useState('');
  const [lookup, setLookup]     = useState<VehicleLookupResult | null>(null);
  const [error, setError]       = useState<string | null>(null);

  const api = createApiClient(getToken);

  // Checked before the request, not after: an impossible plate otherwise costs a call to our
  // API plus a DVSA token fetch and lookup, and comes back as "no DVSA record" - which reads
  // as a missing vehicle rather than the typo it is. The API re-checks either way.
  const regError = reg.trim() ? validateRegistration(reg) : null;

  const runLookup = () => {
    if (regError) { setLookup(null); setError(regError); return; }
    lookupMut.mutate();
  };

  const lookupMut = useMutation({
    mutationFn: () => vehiclesService(api).lookup(normaliseRegistration(reg)),
    onSuccess: (data) => {
      setLookup(data);
      setError(null);
      // Pre-select the type the DVSA make and model imply. Left alone when the API sends
      // nothing back, because a heavy vehicle or a blank make cannot be guessed from that
      // data and a wrong pre-selection reads as "the DVSA said so" and gets clicked past.
      if (data.suggestedVehicleType !== undefined && data.suggestedVehicleType !== null) {
        setVt(data.suggestedVehicleType);
        setTypeFromDvsa(true);
      } else {
        setTypeFromDvsa(false);
      }
    },
    onError: (err: any) => setError(err?.response?.data?.error ?? 'Lookup failed. Please try again.'),
  });

  const saveMut = useMutation({
    mutationFn: () => vehiclesService(api).registerVehicle({
      registration: normaliseRegistration(reg),
      vehicleType,
      nickname: nickname.trim() || undefined,
    }),
    onSuccess: onDone,
    onError: (err: any) => setError(err?.response?.data?.error ?? 'Could not add vehicle.'),
  });

  const canConfirm = lookup !== null && !lookup.alreadyRegistered;

  return (
    <div className="card p-5 space-y-4">
      <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
        <Plus className="w-4 h-4 text-indigo-500" /> Add a Vehicle
      </h2>

      {/* Step 1 — registration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Vehicle registration <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2 flex-wrap sm:flex-nowrap">
          <input
            value={reg}
            onChange={e => { setReg(e.target.value.toUpperCase()); setLookup(null); setError(null); }}
            onKeyDown={e => { if (e.key === 'Enter' && reg.trim()) { e.preventDefault(); runLookup(); } }}
            className="form-input uppercase font-semibold tracking-wider flex-1"
            placeholder="AB12 CDE"
            maxLength={MAX_REGISTRATION_LENGTH + 2}   /* room for a space and a hyphen */
            data-testid="vehicle-registration-input"
          />
          <button
            type="button"
            onClick={runLookup}
            disabled={!reg.trim() || regError !== null || lookupMut.isPending}
            data-testid="vehicle-lookup-button"
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm shrink-0"
          >
            {lookupMut.isPending
              ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Looking up…</>
              : <><Search className="w-4 h-4" />Look up</>
            }
          </button>
        </div>
        {regError ? (
          <p className="text-xs text-red-600 mt-1.5" data-testid="vehicle-registration-error">
            {regError}
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-1.5">
            We fetch the vehicle details and MOT history from the DVSA so you do not have to type them in.
          </p>
        )}
      </div>

      {/* Step 2 — confirmation */}
      {lookup && (
        <div
          data-testid="vehicle-lookup-result"
          className={`rounded-xl border p-4 space-y-3 ${
            lookup.found ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          {lookup.found ? (
            <>
              <div className="flex items-start gap-2">
                <BadgeCheck className="w-5 h-5 text-green-600 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">
                    {lookup.make} {lookup.model}
                  </p>
                  <p className="text-xs text-gray-600">
                    {[lookup.colour, lookup.fuelType,
                      lookup.engineSizeCc ? `${lookup.engineSizeCc}cc` : null,
                      lookup.year].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-700" data-testid="vehicle-lookup-mot-summary">
                {lookup.motSummary}
                {lookup.motTestCount > 0 && (
                  <span className="text-gray-500"> · {lookup.motTestCount} MOT test{lookup.motTestCount === 1 ? '' : 's'} on record</span>
                )}
              </p>
              {lookup.hasOutstandingRecall === 'Yes' && (
                <p className="text-xs text-red-700 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />This vehicle has an outstanding safety recall.
                </p>
              )}
            </>
          ) : (
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-900">{lookup.motSummary ?? lookup.failureReason}</p>
            </div>
          )}

          {lookup.alreadyRegistered && (
            <p className="text-xs text-red-700 flex items-center gap-1 font-medium">
              <XCircle className="w-3.5 h-3.5" />
              This registration is already on another account and cannot be added.
            </p>
          )}
        </div>
      )}

      {/* Step 3 — the bits only the user can tell us */}
      {lookup && !lookup.alreadyRegistered && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Vehicle Type</label>
            <div className="flex gap-2 flex-wrap">
              {VEHICLE_TYPES.map(vt => (
                <button
                  key={vt}
                  type="button"
                  onClick={() => { setVt(vt); setTypeFromDvsa(false); }}
                  data-testid={`vehicle-type-${vt}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    vehicleType === vt
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {VEHICLE_TYPE_LABELS[vt]}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1.5" data-testid="vehicle-type-hint">
              {typeFromDvsa
                ? 'Suggested from the DVSA record — change it if that is wrong.'
                : 'Not shown by the DVSA record — please pick one.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nickname (optional)</label>
            <input
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              className="form-input"
              placeholder="Leeds Van"
              maxLength={100}
              data-testid="vehicle-nickname-input"
            />
          </div>
        </>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="vehicle-form-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => saveMut.mutate()}
          disabled={!canConfirm || saveMut.isPending}
          data-testid="vehicle-confirm-button"
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
        >
          {saveMut.isPending
            ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Saving…</>
            : <><CheckCircle className="w-4 h-4" />Confirm &amp; Save</>
          }
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function VehiclesPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();

  const api = createApiClient(getToken);
  const svc = vehiclesService(api);

  const { data: vehicles = [], isLoading, refetch } = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => svc.getAll(),
    enabled: !!account,
  });

  const { data: myDocs = [], refetch: refetchDocs } = useQuery<DocumentUpload[]>({
    queryKey: ['my-documents'],
    queryFn: () => documentsService(api).getMy(),
    enabled: !!account,
  });

  const uploadsFor = (type: DocumentType) => myDocs.filter(d => d.documentType === type);

  const [showForm, setShowForm] = useState(false);

  const deleteMut = useMutation({
    mutationFn: (id: string) => svc.deactivateVehicle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });

  const expiringSoon = vehicles.filter(v => v.motExpiringSoon || v.insuranceExpiringSoon);

  return (
    <div className="page-shell space-y-6" data-testid="vehicles-page">

      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Truck className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Vehicles</h1>
            <p className="text-sm text-gray-500">
              {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}. MOT is checked automatically with the DVSA.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          data-testid="vehicles-add-button"
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Vehicle
        </button>
      </div>

      {/* Expiry warnings */}
      {expiringSoon.length > 0 && (
        <div className="card p-4 border-amber-200 bg-amber-50 space-y-1" data-testid="vehicles-expiry-warning">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> Action needed soon
          </p>
          {expiringSoon.map(v => (
            <p key={v.id} className="text-xs text-amber-700">
              {v.registration}: {[
                v.motExpiringSoon ? v.motSummary : null,
                v.insuranceExpiringSoon ? 'Insurance expiring' : null,
              ].filter(Boolean).join(' · ')}
            </p>
          ))}
        </div>
      )}

      {showForm && (
        <AddVehicleForm
          onDone={() => { qc.invalidateQueries({ queryKey: ['vehicles'] }); setShowForm(false); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Vehicle list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-6 h-6 border-4 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      ) : vehicles.length === 0 ? (
        <div className="card py-12 text-center text-gray-400" data-testid="vehicles-empty">
          <Truck className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">No vehicles yet</p>
          <p className="text-xs mt-1">Add a vehicle to track its MOT automatically.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vehicles.map(v => (
            <VehicleCard
              key={v.id}
              vehicle={v}
              onDelete={() => deleteMut.mutate(v.id)}
              deleting={deleteMut.isPending && deleteMut.variables === v.id}
              onRefresh={refetch}
            />
          ))}
        </div>
      )}

      {/* Business Insurance */}
      <div className="space-y-3 pt-4 border-t border-gray-200">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Business Insurance</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload your business-level insurance certificates for admin review.
          </p>
        </div>
        {BUSINESS_INSURANCE_DOCS.map(meta => (
          <BusinessInsuranceCard
            key={meta.type}
            meta={meta}
            uploads={uploadsFor(meta.type)}
            getToken={getToken}
            onUploadDone={() => refetchDocs()}
          />
        ))}
      </div>
    </div>
  );
}
